import * as React from 'react';
import Spacing from 'components/Layout/components/Spacing';
import { Card, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import DateRangePickerForm, { DatePickerSettings } from 'components/Input/components/DateRangePickerForm';
import moment from 'moment';
import { FetchBordereauOptions } from 'services/reporting';
import { getRolesContain } from '../../../helpers/roles';
import { useSelector } from 'react-redux';
import { getUserGroups } from '../../../application/redux/selectors';

export interface ReportDownloadOption extends DatePickerSettings {
    entity: string;
    contentType: string;
    filename: string;
    permissions: string[];
    defaultStart?: moment.Moment;
    defaultEnd?: moment.Moment;
}

type Props = {
    options: ReportDownloadOption[];
    fetchDownloadFile: (fetchOptions: FetchBordereauOptions) => Promise<any>;
    title?: string;
};

const ReportDownloadsPanel: React.FC<Props> = ({ options, fetchDownloadFile, title = 'Download Reports' }) => {
    const userGroups = useSelector(getUserGroups);

    const clickAnchorRef = React.useRef<HTMLAnchorElement | null>(null);

    return (
        <>
            <a ref={clickAnchorRef as any} style={{ display: 'none' }} />
            <Card>
                <Grid container spacing={0} alignItems="center">
                    <Grid xs={6}>
                        <Typography variant="h3">{title}</Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={4}>
                    {options
                        .filter((option) => getRolesContain(userGroups, option.permissions))
                        .map((option) => (
                            <Grid key={option.label + 'option'} xs={6}>
                                <Divider />
                                <Spacing height={'5px'} />
                                <DateRangePickerForm
                                    onSubmit={async (values) => {
                                        const fetchOptions: FetchBordereauOptions = {
                                            startDate: values.startDate,
                                            endDate: values.endDate,
                                            contentType: values.option?.contentType,
                                            reportEntity: values.option?.entity,
                                            removeEmptyColumns: values.option?.removeEmptyColumns,
                                            qualifier: values.qualifier,
                                        };
                                        const report = await fetchDownloadFile(fetchOptions);
                                        let fileType = '.json';
                                        const contentType = report.headers.get('Content-Type');
                                        switch (contentType) {
                                            case 'text/csv':
                                                fileType = '.csv';
                                                break;
                                            case 'text/tab-separated-values':
                                                fileType = '.tsv';
                                                break;
                                            default:
                                                break;
                                        }

                                        const blob = await report.blob();
                                        const url = URL.createObjectURL(blob);

                                        const fileDate = values.option?.hideStartDate
                                            ? values.option?.hideEndDate
                                                ? moment()
                                                : values.endDate
                                            : values.startDate;

                                        const endDateStr =
                                            !values.option?.hideEndDate && !values.option?.hideStartDate
                                                ? ` - ${values.endDate ? values.endDate.format('MM/DD/YYYY') : ''}`
                                                : '';

                                        const anchor = clickAnchorRef.current;
                                        if (anchor) {
                                            anchor.href = url;
                                            anchor.download =
                                                (fileDate ? fileDate.format('MM_DD_YYYY') : '') +
                                                endDateStr +
                                                (fileDate ? '_' : '') +
                                                (values.option?.filename || 'Bordereaux') +
                                                (values.option?.addQualifierToFilename && values.qualifier
                                                    ? values.qualifier
                                                    : '') +
                                                fileType;

                                            anchor.click();
                                            URL.revokeObjectURL(url);
                                        }
                                    }}
                                    submitText={'Download Report'}
                                    options={[option]}
                                    labelText={option.label}
                                    defaultStart={option.defaultStart || undefined}
                                    defaultEnd={option.defaultEnd || undefined}
                                />
                                {/*<Spacing height="30px" />*/}
                            </Grid>
                        ))}
                </Grid>
            </Card>
        </>
    );
};

export default ReportDownloadsPanel;
